import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import { ChartLabel, NoData, formatCost } from './shared';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 0.75rem 1.5rem;
  text-align: ${({ align }) => align || 'left'};
  font-weight: ${weights.bold};
  font-size: 0.875rem;
  color: ${colors.grey55};
  border-bottom: solid 2px ${colors.grey10};
`;

const Td = styled.td`
  padding: 0.75rem 1.5rem;
  text-align: ${({ align }) => align || 'left'};
  font-size: 0.875rem;
  border-bottom: solid 1px ${colors.grey10};
`;

const Bar = styled.div`
  width: 10rem;
  height: 0.5rem;
  background-color: ${colors.grey5};
  border-radius: 0.25rem;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background-color: ${colors.primary};
  border-radius: 0.25rem;
`;

export default function Concentration({ start, end }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getConcentration({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  if (isLoading || !data) return null;

  if (data.tiers.length === 0) {
    return (
      <div>
        <ChartLabel>Cost Concentration</ChartLabel>
        <NoData>No data available for the selected period.</NoData>
      </div>
    );
  }

  return (
    <div>
      <ChartLabel>Cost Concentration</ChartLabel>
      <Table>
        <thead>
          <tr>
            <Th>Segment</Th>
            <Th align="right">Members</Th>
            <Th align="right">Cost</Th>
            <Th align="right">% of Total Cost</Th>
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {data.tiers.map((tier) => (
            <tr key={tier.label}>
              <Td>{tier.label}</Td>
              <Td align="right">{tier.members}</Td>
              <Td align="right">{formatCost(tier.cost)}</Td>
              <Td align="right">{(tier.percentOfCost * 100).toFixed(0)}%</Td>
              <Td>
                <Bar>
                  <BarFill style={{ width: `${tier.percentOfCost * 100}%` }} />
                </Bar>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
